import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import CampaignIcon from '@material-ui/icons/RecordVoiceOver';

import { getMessagesList } from '../../../../api/messages';
import { ModalDefaultDraggable } from '../../../../molecules';
import {
  sanitizeHtml,
  htmlToPlainText,
} from '../../../../utils/sanitizeHtml';
import { richMessageStyles } from '../../../../molecules/RichMessageText';

/**
 * Marketing-style notification for the client portal header.
 *
 * The sender is deliberately never rendered. A Branch Admin composes these in
 * Settings > Messages, but to the client they read as an announcement from
 * Elite Permits, not as mail from a named person - so `sender` and
 * `addressesList` are ignored even though the API returns them.
 *
 * There is deliberately no dismiss control. An announcement is not the
 * client's to delete, and messages already expire on their own - the API only
 * ever returns today's.
 *
 * Scoping is entirely server-side: the client rule in MessageViewSet already
 * limits the list to today, the client's own branch, and messages either
 * broadcast or addressed to them. Nothing needs filtering here.
 */

interface MarketingItem {
  id: number;
  /** Sanitised HTML, safe for dangerouslySetInnerHTML. */
  html: string;
  /** Markup-free version for the single-line banner preview. */
  preview: string;
}

const MarketingMessage = () => {
  const [items, setItems] = React.useState<MarketingItem[]>(
    [],
  );
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    getMessagesList({ limit: 25, offset: 0 })
      .then(response => {
        if (cancelled) return;
        const rows = (response.data?.results || [])
          .filter(row => row && row.text)
          .map(row => ({
            id: row.id,
            // Message bodies are admin-authored HTML. Never render them raw.
            html: sanitizeHtml(row.text),
            preview: htmlToPlainText(row.text),
          }))
          // A body that sanitises down to nothing is not worth a banner.
          .filter(row => row.preview.length > 0);
        setItems(rows);
      })
      // A client with no messages gets a 200 and an empty list; anything else
      // (network, permissions) should stay silent rather than break the header.
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  if (!items.length) return null;

  // The banner previews the newest; the modal lists them all.
  const current = items[0];

  return (
    <>
      <Banner
        type="button"
        onClick={() => setIsOpen(true)}
        title="View announcement"
      >
        <IconBadge>
          <CampaignIcon />
        </IconBadge>
        <BannerBody>
          <BannerEyebrow>
            Announcement
            {items.length > 1 && ` · ${items.length}`}
          </BannerEyebrow>
          <BannerText>{current.preview}</BannerText>
        </BannerBody>
      </Banner>

      {isOpen && (
        <ModalDefaultDraggable
          title="Announcement"
          narrow
          centered
          onClose={() => setIsOpen(false)}
        >
          <ModalBody>
            {items.map(item => (
              <Announcement key={item.id}>
                <AnnouncementIcon>
                  <CampaignIcon />
                </AnnouncementIcon>
                <AnnouncementText
                  dangerouslySetInnerHTML={{
                    __html: item.html,
                  }}
                />
              </Announcement>
            ))}
          </ModalBody>
        </ModalDefaultDraggable>
      )}
    </>
  );
};

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.28); }
  70%  { box-shadow: 0 0 0 8px rgba(37, 99, 235, 0); }
  100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
`;

const Banner = styled.button`
  /* Grows into the free space, but capped: at 75% it squeezed the greeting
     down to "Welcome back, C...". The steps below shed width before the
     greeting has to truncate. */
  flex: 1 1 auto;
  min-width: 0;
  max-width: 780px;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  height: 48px;
  padding: 0 16px 0 10px;
  border: 1px solid var(--primary-100);
  border-radius: 10px;
  background: linear-gradient(
    135deg,
    var(--primary-50) 0%,
    var(--neutral-0) 78%
  );
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    transform 0.15s ease;

  &:hover {
    border-color: var(--primary-600);
    box-shadow: var(--shadow-xs);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  @media only screen and (max-width: 1500px) {
    max-width: 600px;
  }

  @media only screen and (max-width: 1280px) {
    max-width: 420px;
  }

  @media only screen and (max-width: 1100px) {
    max-width: 300px;
  }

  /* Tight headers keep the icon as the entry point; the text would be
     unreadable at this width anyway and the modal still has the full copy. */
  @media only screen and (max-width: 720px) {
    flex: 0 0 auto;
    max-width: none;
    width: 44px;
    padding: 0;
    justify-content: center;
  }
`;

const IconBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  animation: ${pulse} 2.6s ease-out infinite;

  svg {
    font-size: 1.125rem;
  }
`;

const BannerBody = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;

  @media only screen and (max-width: 720px) {
    display: none;
  }
`;

const BannerEyebrow = styled.span`
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--primary-700);
  line-height: 1.2;
`;

const BannerText = styled.span`
  margin-top: 1px;
  font-size: 0.8125rem;
  color: var(--mainText);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: left;
`;

const Announcement = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--primary-100);
  border-radius: 10px;
  background: linear-gradient(
    135deg,
    var(--primary-50) 0%,
    var(--neutral-0) 70%
  );
  box-sizing: border-box;
`;

const AnnouncementIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);

  svg {
    font-size: 1.125rem;
  }
`;

const AnnouncementText = styled.div`
  min-width: 0;
  font-size: 0.9375rem;
  line-height: 1.55;
  color: var(--mainText);
  overflow-wrap: anywhere;

  ${richMessageStyles}

  /* Announcements get real block spacing; the table cell stays tight. */
  > * + * {
    margin-top: 8px;
  }
`;

export default MarketingMessage;
